import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { Upload, X } from "lucide-react";
import AuthImage from "@/components/AuthImage";

/**
 * Upload one or more files. Calls `onChange(fileIds)` with the list of uploaded file_ids.
 * `value` is an array of file_ids.
 */
export default function FileUploader({ value = [], onChange, accept = "image/*,video/*", max = 8, label = "Upload photos" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    const remaining = max - value.length;
    const selected = Array.from(files).slice(0, remaining);
    if (selected.length === 0) return;
    setUploading(true);
    const added = [];
    for (const f of selected) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/files/upload", fd);
        added.push(data.file_id);
      } catch (e) {
        console.error("upload fail", e);
      }
    }
    setUploading(false);
    onChange([...value, ...added]);
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {value.map((id) => (
          <div key={id} className="relative h-20 w-20 rounded-sm overflow-hidden border border-zinc-800 bg-zinc-950 group">
            <AuthImage fileId={id} className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => onChange(value.filter((v) => v !== id))}
              className="absolute top-1 right-1 bg-zinc-950/90 rounded-sm p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`remove-photo-${id}`}
            >
              <X className="w-3 h-3 text-red-400" />
            </button>
          </div>
        ))}
        {value.length < max && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="h-20 w-20 border border-dashed border-zinc-700 hover:border-orange-500 rounded-sm flex flex-col items-center justify-center text-zinc-500 hover:text-orange-500 transition-colors"
            data-testid="upload-trigger"
          >
            <Upload className="w-4 h-4 mb-1" />
            <span className="text-[9px] uppercase tracking-widest">
              {uploading ? "..." : "Upload"}
            </span>
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-600 mt-2">{label} · {value.length}/{max}</div>
    </div>
  );
}
